import { Suspense, useMemo, useState } from 'react';
import { Download, ChevronDown } from 'lucide-react';
import { getSDK } from '@/store/sdkStore';
import { Button } from '@/components/ui/button';
import { settingsStore } from '@/lib/settingsStore';
import { resolveWithDefault, handlersFor, getHandlerById, type ContentHandler } from '@/lib/contentRegistry';
import { fileExt } from '@/lib/fileName';
import { bytesToBlob } from '@/lib/fileBytes';
import { ensureBuiltinContentHandlers } from '@/components/viewer/builtinContentHandlers';

// ============================================================================
// ContentViewer — ЕДИНЫЙ просмотрщик файла через реестр обработчиков.
// - Обработчик по умолчанию: user_settings `content.default_app.{ext}` (первый
//   выбор пользователя через «Открыть с помощью…» запоминается); иначе —
//   последний зарегистрированный (переопределение приложением).
// - Несколько обработчиков формата → меню «Открыть с помощью…» (переключает вид).
// ============================================================================

export interface ContentViewerProps {
  fileId: string;
  fileName: string;
  fileType?: string;
  className?: string;
  height?: number | string;
}

export function ContentViewer({ fileId, fileName, fileType, className, height = '100%' }: ContentViewerProps) {
  ensureBuiltinContentHandlers();
  const ext = fileExt(fileName);
  const [preferredId, setPreferredId] = useState<string | undefined>(() =>
    settingsStore.get(`content.default_app.${ext}`),
  );
  const handler = useMemo(() => resolveWithDefault(ext, preferredId), [ext, preferredId]);
  const alternatives = useMemo(() => handlersFor(ext), [ext]);

  const props = { fileId, fileName, fileType };
  const style: React.CSSProperties = { height, minHeight: 140 };

  // Выбор обработчика: запоминаем предпочтение и переключаем вид.
  const chooseHandler = (id: string) => {
    setPreferredId(id);
    settingsStore.set(`content.default_app.${ext}`, id);
  };

  if (!handler) {
    return (
      <div className="flex h-full min-h-[160px] flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
        <span>Нет встроенного просмотра для <b>.{ext || '?'}</b></span>
        <Button size="sm" variant="outline" onClick={() => void downloadFile(fileId)}>
          <Download className="mr-1 h-4 w-4" />Скачать
        </Button>
      </div>
    );
  }

  const View = handler.view;
  const canChoose = alternatives.length > 1;
  return (
    <div className={className} style={style}>
      <Suspense fallback={<div className="p-4 text-sm text-muted-foreground">Загрузка…</div>}>
        <View {...props} />
        {canChoose && (
          <div className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground">
            <span>Открыто: <b>{handler.label}</b></span>
            <span className="inline-flex items-center gap-0.5">
              <ChevronDown className="h-3 w-3" />
              <select
                className="cursor-pointer rounded border-none bg-transparent text-[10px] outline-none"
                value={handler.id}
                onChange={(e) => chooseHandler(e.target.value)}
              >
                <option disabled>Открыть с помощью…</option>
                {alternatives.map((alt) => (
                  <option key={alt.id} value={alt.id}>{alt.label}</option>
                ))}
              </select>
            </span>
          </div>
        )}
      </Suspense>
    </div>
  );
}

// downloadFile — скачать файл и сохранить (через SDK файлов).
export async function downloadFile(fileId: string): Promise<void> {
  const r: any = await getSDK().files.downloadFile({ id: fileId });
  const blob = bytesToBlob(r?.data ?? r?.content);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `file-${fileId}`;
  a.click();
  URL.revokeObjectURL(url);
}

export type { ContentHandler };
export { getHandlerById };
